import React from 'react'
import { Badge, Group, Stack, Text } from '@mantine/core'
import {
  BackpackIcon,
  CalendarIcon,
  ChatBubbleIcon,
  ClockIcon,
  FrameIcon,
  GlobeIcon,
  HomeIcon,
  PersonIcon,
  ReaderIcon,
  SewingPinIcon,
} from '@radix-ui/react-icons'
import classNames from 'classnames'
import addMinutes from 'date-fns/addMinutes'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import isFuture from 'date-fns/isFuture'
import isSameDay from 'date-fns/isSameDay'
import parseISO from 'date-fns/parseISO'

import { View } from './Calendar'
import {
  Dot,
  NoticeBox,
  useEventStyles,
  usePopoverStyles,
} from './CalendarEvent'
import { EventPopover } from './EventPopover'

import {
  CalendarCourtReporterAdminEvent,
  CalendarCourtReporterEvent,
  CourtReporterSource,
  EventFormat,
} from '@/apiClient'
import { AttorneyList } from '@/components/Calendar/components/AttorneyList'
import { DataEntry } from '@/components/DataEntry'
import { BookCourtReporterButton } from '@/components/Event/BookCourtReporterButton'
import { CancelCourtReporterBookingButton } from '@/components/Event/CancelCourtReporterBookingButton'
import { EventDetailButton } from '@/components/Event/EventDetailButton'
import { getAttorneys, getEventFormat } from '@/utils/parrot'
import { STATES } from '@/utils/states'
import {
  formatDate,
  formatDuration,
  formatTime,
  formatTimeRange,
  formatTimezone,
  getCalendarEventTimezone,
} from '@/utils/time'

type CourtReporterEvent = CalendarCourtReporterAdminEvent
type CourtReporterStatus = CalendarCourtReporterAdminEvent['general_status']
type PreviewEvent = CalendarCourtReporterAdminEvent | CalendarCourtReporterEvent

export const CourtReporterCalendarEvent: React.FC<{
  event: CourtReporterEvent
  view: View
  onChange?: () => void
}> = ({ event, view, onChange }) => {
  const popoverStyles = usePopoverStyles(view)
  const status = event.general_status
  const courtReporterName =
    event.court_reporter_booking?.court_reporter_name ?? 'Unassigned'

  const Preview = {
    month: SmallPreview,
    week: LargePreview,
    day: LargePreview,
    list: InlinePreview,
    listCustom: InlinePreview,
  }[view]

  return (
    <EventPopover
      handle={
        <Preview
          event={event}
          status={status}
          courtReporterName={courtReporterName}
        />
      }
      header={<CourtReporterEventHeader event={event} status={status} />}
      position={popoverStyles.position}
    >
      <>
        <Group spacing="md">
          <CourtReporterEventEntries
            event={event}
            courtReporterName={courtReporterName}
          />

          <Group mb="xl" spacing="xs" className="w-full">
            {event.court_reporter_source === CourtReporterSource.client_own && (
              <NoticeBox>
                The client provides their own court reporter.
              </NoticeBox>
            )}

            {!event.court_reporter_source && (
              <NoticeBox>The court reporter source is not set yet.</NoticeBox>
            )}

            {event.general_status === 'cancelled' && (
              <NoticeBox>This event was canceled.</NoticeBox>
            )}

            {event.status === 'upcoming' &&
              isFuture(parseISO(event.datetime)) && (
                <NoticeBox>
                  Event is starting in{' '}
                  {formatDistanceToNow(parseISO(event.datetime))}
                </NoticeBox>
              )}
          </Group>
        </Group>

        <Group position="right">
          {(event.general_status === 'booked' ||
            event.general_status === 'requested') && (
            <CancelCourtReporterBookingButton
              event={event}
              onCancel={() => onChange?.()}
            />
          )}

          <EventDetailButton
            event={event}
            to={`/admin/court-reporter-event/${event.id}`}
          />

          {event.general_status === 'available' && (
            <BookCourtReporterButton
              event={event}
              onBook={() => onChange?.()}
            />
          )}
        </Group>
      </>
    </EventPopover>
  )
}

export const CourtReporterEventHeader: React.FC<{
  event: PreviewEvent
  status: CourtReporterStatus
}> = ({ event, status }) => (
  <Stack spacing="xs">
    <Text>{event.name}</Text>
    <CourtReporterEventBadges event={event} status={status} />
  </Stack>
)

export const CourtReporterEventBadges: React.FC<{
  event: Pick<PreviewEvent, 'type' | 'event_format'>
  status: CourtReporterStatus
}> = ({ event, status }) => (
  <Group spacing="sm">
    <Badge color="gray">
      {/* showing EUO because Examination Under Oath is too long */}
      {event.type.replace('examination_under_oath', 'euo').replaceAll('_', ' ')}
    </Badge>
    <CourtReporterEventStatusBadge status={status} />
    <Badge color="blue">
      {getEventFormat(event) === EventFormat.on_site ? 'In person' : 'Remote'}
    </Badge>
  </Group>
)

export const CourtReporterEventEntries: React.FC<{
  event: PreviewEvent
  courtReporterName?: string
}> = ({ event, courtReporterName }) => {
  const location = formatOnSiteLocation(event)
  const tz = getCalendarEventTimezone(event)

  return (
    <Stack ml="xs" spacing="xs">
      <DataEntry icon={CalendarIcon} tooltip="Date">
        {formatDate(event.datetime, { long: true, separator: ' ', tz })}
      </DataEntry>

      <DataEntry icon={ClockIcon} tooltip="Time">
        {formatTimeRange(event.datetime, event.duration, { tz })}{' '}
        {formatTimezone(tz, event.datetime)} ({formatDuration(event.duration)})
      </DataEntry>

      {courtReporterName !== undefined && (
        <DataEntry icon={PersonIcon} tooltip="Court reporter">
          {courtReporterName}
        </DataEntry>
      )}

      {location && (
        <DataEntry icon={SewingPinIcon} tooltip="Address">
          {location}
        </DataEntry>
      )}

      <DataEntry icon={HomeIcon} tooltip="Organization">
        {event.team.organization.name}: {event.team.name}
      </DataEntry>

      <DataEntry icon={BackpackIcon} tooltip="Attorneys">
        <AttorneyList attorneys={getAttorneys(event)} />
      </DataEntry>

      <DataEntry
        icon={ReaderIcon}
        tooltip={event.case.is_claim ? 'Claim' : 'Case'}
      >
        {event.case.name}
      </DataEntry>

      {(event.case.number || event.claim_number) && (
        <DataEntry
          icon={FrameIcon}
          tooltip={event.case.number ? 'Case number' : 'Claim number'}
        >
          {event.case.number || event.claim_number}
        </DataEntry>
      )}

      {event.case.state && (
        <DataEntry icon={GlobeIcon} tooltip="Location">
          {event.case.state in STATES
            ? STATES[event.case.state]
            : event.case.state}
          {event.case.county ? `: ${event.case.county}` : ''}
        </DataEntry>
      )}

      {event.recording.is_translator_present && (
        <DataEntry icon={ChatBubbleIcon} tooltip="Interpreter">
          Interpreter present
        </DataEntry>
      )}
    </Stack>
  )
}

const CourtReporterEventStatusBadge: React.FC<{
  status: CourtReporterStatus
}> = ({ status }) => {
  const { color, label, variant } = {
    available: { color: 'green', label: 'Available to book' },
    requested: { color: 'indigo', label: 'Requested', variant: 'outline' },
    booked: { color: 'indigo', label: 'Booked event' },
    past: { color: 'gray', label: 'Past event' },
    cancelled: { color: 'gray', label: 'Canceled' },
    unavailable: { color: 'gray', label: 'Unavailable' },
  }[status] as {
    color: string
    label: string
    variant?: 'outline'
  }

  return (
    <Badge color={color} variant={variant}>
      {label}
    </Badge>
  )
}

export const formatOnSiteLocation = (
  event: Pick<PreviewEvent, 'event_format' | 'on_site_location'>,
): string | null => {
  const location = event.on_site_location

  if (getEventFormat(event) !== EventFormat.on_site || !location) {
    return null
  }

  const stateZip = [location.state, location.zip].filter(Boolean).join(' ')

  return (
    [location.address, location.city, stateZip].filter(Boolean).join(', ') ||
    null
  )
}

type PreviewProps = {
  event: PreviewEvent
  status: CourtReporterStatus
  courtReporterName?: string
}

export const SmallPreview: React.FC<PreviewProps> = ({ event, status }) => {
  const styles = useEventStyles()

  return (
    <div className="px-1">
      <div
        className={classNames(
          'inline-flex items-center w-full overflow-hidden px-1 space-x-1 rounded hover:bg-gray-100 text-sm cursor-pointer',
          {
            'border border-gray-300': !isSameDay(
              parseISO(event.datetime),
              addMinutes(parseISO(event.datetime), event.duration),
            ),
          },
        )}
      >
        <div className="mr-1">
          <Dot style={styles[status]} />
        </div>

        <Text color="gray" size="xs">
          {formatTime(event.datetime)}
        </Text>

        <Text size="xs" weight={500} className="truncate">
          {event.name}
        </Text>
      </div>
    </div>
  )
}

export const LargePreview: React.FC<PreviewProps> = ({ event, status }) => {
  const styles = useEventStyles()
  const attorneys = getAttorneys(event)

  return (
    <div className="border border-white bg-white rounded h-full relative">
      <div
        className="w-full h-full p-1 rounded cursor-pointer border text-xs text-ellipsis overflow-hidden"
        style={{ ...styles[status], minHeight: 25 }}
      >
        {event.duration <= 60 && (
          <div>
            <span className="font-semibold truncate">{event.name}</span>,{' '}
            {formatTime(event.datetime)}
          </div>
        )}

        {event.duration > 60 && (
          <div>
            <div className="font-semibold truncate">{event.name}</div>
            <div>{formatTime(event.datetime)}</div>
            {attorneys.length > 0 && (
              <div className="truncate">
                {attorneys.map(attorney => attorney.name).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export const InlinePreview: React.FC<PreviewProps> = ({
  event,
  status,
  courtReporterName,
}) => {
  const styles = useEventStyles()
  const attorneys = getAttorneys(event)
  const tz = getCalendarEventTimezone(event)

  return (
    <div className="mx-16 flex items-center max-w-full px-1 space-x-5 hover:bg-gray-100 cursor-pointer">
      <div className="mr-1">
        <Dot style={styles[status]} />
      </div>

      <div className="w-40">
        <Text
          color="gray"
          size="sm"
          sx={theme => ({ color: theme.colors.gray[6] })}
        >
          {formatTimeRange(event.datetime, event.duration, { tz })}
        </Text>
        <Text size="xs" color="dimmed">
          {formatTimezone(tz, event.datetime)}
        </Text>
      </div>

      <div className="w-24">
        <Text
          color="gray"
          size="xs"
          sx={theme => ({ color: theme.colors.gray[6] })}
        >
          {attorneys.map(attorney => attorney.name).join(', ') || 'No Attorney'}
        </Text>
      </div>

      {courtReporterName !== undefined && (
        <div className="w-44">
          <Text size="sm">{courtReporterName}</Text>
        </div>
      )}

      <Text size="sm">{event.name}</Text>
    </div>
  )
}
